/**
 * Renderer helpers for ~/.aiee/mcp through the Electron preload bridge.
 * Desktop stdio MCP servers spawn on this computer; the backend only sees
 * a catalog + tool-call proxy over the existing Socket.IO session.
 */

import type { DesktopMcpServer, DesktopMcpTool } from '../electron';
import type { McpServerConfig } from '../services/mcp.service';

export const DESKTOP_MCP_ID_PREFIX = 'desktop:';

export function canManageDesktopMcp(): boolean {
    return typeof window !== 'undefined'
        && typeof window.electronAPI?.listMcpServers === 'function'
        && typeof window.electronAPI?.saveMcpServer === 'function';
}

export function isDesktopMcpServerId(id?: string): boolean {
    return Boolean(id && id.startsWith(DESKTOP_MCP_ID_PREFIX));
}

export async function listDesktopMcpServers(): Promise<DesktopMcpServer[]> {
    if (!canManageDesktopMcp() || !window.electronAPI?.listMcpServers) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.listMcpServers();
}

export async function saveDesktopMcpServer(server: Partial<DesktopMcpServer>): Promise<DesktopMcpServer> {
    if (!window.electronAPI?.saveMcpServer) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.saveMcpServer(server);
}

export async function deleteDesktopMcpServer(id: string): Promise<string> {
    if (!window.electronAPI?.deleteMcpServer) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.deleteMcpServer(id);
}

export async function setDesktopMcpServerEnabled(id: string, enabled: boolean): Promise<DesktopMcpServer> {
    if (!window.electronAPI?.setMcpServerEnabled) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.setMcpServerEnabled(id, enabled);
}

export async function testDesktopMcpServer(id: string): Promise<DesktopMcpServer> {
    if (!window.electronAPI?.testMcpServer) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.testMcpServer(id);
}

export async function callDesktopMcpTool(
    serverId: string,
    toolName: string,
    toolArguments: Record<string, unknown> = {}
): Promise<unknown> {
    if (!window.electronAPI?.callMcpTool) {
        throw new Error('Desktop MCP is only available in the desktop app');
    }
    return window.electronAPI.callMcpTool({
        serverId,
        toolName,
        arguments: toolArguments
    });
}

function toCatalogServer(server: DesktopMcpServer) {
    return {
        id: `${DESKTOP_MCP_ID_PREFIX}${server.id}`,
        name: server.name,
        description: server.description,
        enabled: true,
        command: server.command,
        args: server.args || [],
        timeoutMs: server.timeoutMs,
        lastStatus: server.lastStatus,
        lastError: server.lastError,
        lastConnectedAt: server.lastConnectedAt,
        tools: server.tools || []
    };
}

export function desktopServerToComposerConfig(server: DesktopMcpServer): McpServerConfig {
    const now = new Date().toISOString();
    return {
        id: `${DESKTOP_MCP_ID_PREFIX}${server.id}`,
        name: server.name,
        companyId: '__desktop__',
        scope: 'desktop',
        description: server.description,
        enabled: server.enabled,
        ownerEnabled: server.enabled,
        effectiveEnabled: server.enabled,
        subordinatePolicy: 'default-enabled',
        policySource: 'owner',
        canSetOwnEnabled: true,
        transport: 'stdio',
        command: server.command,
        args: server.args || [],
        env: server.env || {},
        headers: {},
        timeoutMs: server.timeoutMs,
        lastStatus: (server.lastStatus as McpServerConfig['lastStatus']) || 'untested',
        lastError: server.lastError || undefined,
        lastConnectedAt: server.lastConnectedAt || undefined,
        tools: (server.tools || []) as DesktopMcpTool[],
        createdAt: now,
        updatedAt: now
    };
}

export async function ackDesktopMcpRequest(
    payload: any,
    ack: (resp: { ok: boolean; result?: any; error?: any }) => void
): Promise<void> {
    if (!canManageDesktopMcp()) {
        ack({ ok: false, error: 'desktop-mcp-unavailable' });
        return;
    }

    try {
        const action = payload?.action;
        if (action === 'list') {
            const servers = await listDesktopMcpServers();
            ack({
                ok: true,
                result: {
                    servers: servers.filter((server) => server.enabled).map(toCatalogServer)
                }
            });
            return;
        }
        if (action === 'call') {
            const result = await callDesktopMcpTool(
                payload.serverId,
                payload.toolName,
                payload.arguments || {}
            );
            ack({ ok: true, result: { result } });
            return;
        }
        throw new Error(`Unsupported desktop MCP action: ${String(action || '')}`);
    } catch (error: any) {
        ack({ ok: false, error: error?.message || String(error) });
    }
}
